import React, { useEffect, useRef, useState } from 'react';
import { Minus, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { analytics, FILTER_CHANGED, LEAD_GEN_PARTY_SIZE_SELECTED } from '../../../helpers/analytics';
import { useFilterAnalytics } from '../../../helpers/analytics/filterContext';

export interface MobilePartySizeStepperProps {
	value: number;
	max: number;
	onChange: (next: number) => void;
	leadGenValue?: number | null;
}

const HOLD_DELAY_MS = 400;
const REPEAT_INTERVAL_MS = 120;

export const MobilePartySizeStepper = ({ value, max, onChange, leadGenValue }: MobilePartySizeStepperProps) => {
	const { t } = useTranslation();
	const filterCtx = useFilterAnalytics('global explore');

	const valueRef = useRef(value);
	valueRef.current = value;
	const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const repeatTimer = useRef<ReturnType<typeof setInterval> | null>(null);

	const setValue = (next: number) => {
		if (next < 0 || next > max) {
			return false;
		}
		valueRef.current = next;
		onChange(next);
		analytics.track(FILTER_CHANGED, {
			filterName: 'groupSize',
			filterValue: next,
			source: 'open_parameters_stepper',
			...filterCtx
		});
		if (leadGenValue !== null && next === leadGenValue) {
			analytics.track(LEAD_GEN_PARTY_SIZE_SELECTED, {
				source: 'open_parameters_stepper',
				partySize: next
			});
		}
		return true;
	};

	const clearTimers = () => {
		if (holdTimer.current) {
			clearTimeout(holdTimer.current);
			holdTimer.current = null;
		}
		if (repeatTimer.current) {
			clearInterval(repeatTimer.current);
			repeatTimer.current = null;
		}
	};

	useEffect(() => clearTimers, []);

	const step = (dir: 1 | -1) => setValue(valueRef.current + dir);

	const startHold = (dir: 1 | -1) => {
		clearTimers();
		step(dir);
		holdTimer.current = setTimeout(() => {
			repeatTimer.current = setInterval(() => {
				if (!step(dir)) {
					clearTimers();
				}
			}, REPEAT_INTERVAL_MS);
		}, HOLD_DELAY_MS);
	};

	const handleKeyStep = (e: React.KeyboardEvent, dir: 1 | -1) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			step(dir);
		}
	};

	const [editing, setEditing] = useState(false);
	const [draft, setDraft] = useState('');

	const startEditing = () => {
		setDraft(String(value));
		setEditing(true);
	};

	const commitDraft = () => {
		const parsed = parseInt(draft, 10);
		if (!Number.isNaN(parsed)) {
			const clamped = Math.min(Math.max(parsed, 0), max);
			if (clamped !== value) {
				setValue(clamped);
			}
		}
		setEditing(false);
	};

	const label = value === 1
		? t('labels.guest', { defaultValue: 'Guest' })
		: t('labels.guests', { defaultValue: 'Guests' });

	return (
		<div className="flex items-center justify-between rounded-2xl border border-tablz-med-gray-4 bg-tablz-med-gray-2 px-4 py-3">
			<button
				type="button"
				aria-label={t('actions.decrease_party_size', { defaultValue: 'Decrease party size' })}
				disabled={value <= 0}
				onPointerDown={(e) => {
 e.preventDefault(); startHold(-1);
}}
				onPointerUp={clearTimers}
				onPointerLeave={clearTimers}
				onPointerCancel={clearTimers}
				onKeyDown={(e) => handleKeyStep(e, -1)}
				className="flex h-10 w-10 items-center justify-center rounded-full text-tablz-text-white-1 disabled:opacity-30"
			>
				<Minus size={20} />
			</button>
			{editing ? (
				<input
					data-testid="party-size-input"
					type="number"
					inputMode="numeric"
					pattern="[0-9]*"
					min={0}
					max={max}
					autoFocus
					value={draft}
					onChange={(e) => setDraft(e.target.value)}
					onBlur={commitDraft}
					onKeyDown={(e) => {
						if (e.key === 'Enter') {
							e.preventDefault();
							commitDraft();
						}
 else if (e.key === 'Escape') {
							e.preventDefault();
							setEditing(false);
						}
					}}
					aria-label={t('titles.party_size', { defaultValue: 'Party size' })}
					className="w-24 bg-transparent text-center font-poppins text-lg font-medium text-tablz-text-white-1 outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
				/>
			) : (
				<button
					type="button"
					data-testid="party-size-value"
					onClick={startEditing}
					className="font-poppins text-lg font-medium text-tablz-text-white-1"
				>
					{leadGenValue !== null && value === leadGenValue ? `${value}+` : `${value} ${label}`}
				</button>
			)}
			<button
				type="button"
				aria-label={t('actions.increase_party_size', { defaultValue: 'Increase party size' })}
				disabled={value >= max}
				onPointerDown={(e) => {
 e.preventDefault(); startHold(1);
}}
				onPointerUp={clearTimers}
				onPointerLeave={clearTimers}
				onPointerCancel={clearTimers}
				onKeyDown={(e) => handleKeyStep(e, 1)}
				className="flex h-10 w-10 items-center justify-center rounded-full text-tablz-text-white-1 disabled:opacity-30"
			>
				<Plus size={20} />
			</button>
		</div>
	);
};
